import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import {
  ComplianceSnapshotFragment,
  FinanceFinancialDocumentComplianceExtraFieldsConfig,
  SaleInvoiceDetailFragment,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { SaleInvoiceDocument, SalesInvoiceProject } from 'modules/finance/lib/salesInvoiceUtils'

import { countryComplianceExtraFieldsConfig } from '../SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'
import ComplianceSnapshots from './_ComplianceSnapshots'
import LineItems from './_LineItems'
import RelatedFinancialDocuments from './_RelatedFinancialDocuments'
import Summary from './_Summary'

interface MainContentProps {
  locale: string
  saleInvoice: SaleInvoiceDetailFragment
  complianceExtraFieldsConfigs?: FinanceFinancialDocumentComplianceExtraFieldsConfig[]
  relatedFinancialDocumentPath: (relatedInvoiceId: number) => string
  complianceSnapshots?: ComplianceSnapshotFragment[]
  relatedProject?: SalesInvoiceProject
}

const MainContent = ({
  locale,
  saleInvoice,
  complianceExtraFieldsConfigs = [],
  relatedFinancialDocumentPath,
  complianceSnapshots = [],
  relatedProject,
}: MainContentProps) => {
  const saleInvoiceDocument: SaleInvoiceDocument = {
    ...saleInvoice,
    issuerCountryCode: saleInvoice.issuerCountryCode || saleInvoice.legalEntity?.country,
  }

  const invoiceComplianceExtraFieldsConfig = useUnsafeMemo(() => {
    return countryComplianceExtraFieldsConfig(
      complianceExtraFieldsConfigs,
      saleInvoiceDocument.issuerCountryCode || undefined
    )
  }, [complianceExtraFieldsConfigs, saleInvoiceDocument.issuerCountryCode])

  return (
    <Box gap="s24" flexDirection="column" paddingBottom="s48">
      <SectionHeader title={i18n.t('finance.sale_invoices.detail.summary')} description={''} />
      <Box gap="s16" width="full" paddingLeft="s20">
        <Summary
          saleInvoice={saleInvoiceDocument}
          complianceExtraFieldsConfigsByCountry={invoiceComplianceExtraFieldsConfig}
          relatedProject={relatedProject}
        />
        <LineItems
          locale={locale}
          saleInvoice={saleInvoiceDocument}
          countryComplianceExtraFieldsConfig={invoiceComplianceExtraFieldsConfig}
        />
      </Box>

      {saleInvoice.financialDocumentEvents.length > 0 && (
        <>
          <SectionHeader
            title={i18n.t('finance.sale_invoices.detail.related_financial_documents.title')}
            description={''}
          />
          <Box gap="s16" width="full" paddingLeft="s20">
            <RelatedFinancialDocuments
              locale={locale}
              saleInvoice={saleInvoice}
              relatedFinancialDocumentPath={relatedFinancialDocumentPath}
            />
          </Box>
        </>
      )}

      {complianceSnapshots.length > 0 && (
        <>
          <SectionHeader
            title={i18n.t('finance.sale_invoices.detail.compliance_snapshots.title')}
            description={''}
          />
          <Box gap="s16" width="full" paddingLeft="s20">
            <ComplianceSnapshots
              complianceSnapshots={complianceSnapshots}
              complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default MainContent
